import { Action } from 'pkg/api/models/auto/action';
import type { Group , Flags} from 'pkg/api/models/group';
import { Features, OrganizationType } from 'pkg/api/models/group';
import type { VideoSource } from 'pkg/api/models/video';

export function isOrganization(group: Group) {
	return group?.organizationId === null;
}

export function isNetworkAdmin(group: Group) {
	return (
		group?.organizationType === OrganizationType.Network &&
		(group.allowedActions ?? []).includes(Action.OrganizationViewNetwork)
	);
}

export function isTopLevelGroup(group: Group) {
	return (
		!isOrganization(group) && group?.parentGroupId === group?.organizationId
	);
}

export function canViewTrainingStatisticsTab(group: Group) {
	return (
		!!group?.id &&
		!isOrganization(group) &&
		!isTopLevelGroup(group) &&
		(group.allowedActions ?? []).includes(
			Action.GroupShowTrainingSessionStatistics
		)
	);
}

export function isLOKActive(group: Group) {
	return !isOrganization(group) ? false : !!group.lokActive;
}

/**
 * Whether the LOK Swedish personnummer field should be offered: only for
 * Swedish organizations with LOK active. Centralised so the form builder and
 * submission flow share one definition of the rule.
 */
export function showsLokPersonalIdField(group?: Group) {
	return !!group && isLOKActive(group) && group.country?.code === 'SE';
}

export function hasFlag(group: Group, flag: Flags): boolean {
	return group?.flags?.includes(flag) ?? false;
}

export function hasFeature(group: Group, feature: Features): boolean {
	return group?.features?.includes(feature);
}

export function hasFeatures(group: Group, ...features: Features[]): boolean {
	return (
		features.length > 0 &&
		features.filter((feature: Features) => group.features.includes(feature))
			.length === features.length
	);
}

export function hasAnyFeature(group: Group, ...features: Features[]): boolean {
	return !!features.filter((feature: Features) =>
		group.features.includes(feature)
	).length;
}

export function hasVideoSource(
	group: Group,
	videoSource: VideoSource
): boolean {
	return (
		hasFeature(group, Features.Video) &&
		group.videoSources.includes(videoSource)
	);
}
